'use client';

import { useEffect, useState } from 'react';
import { ReviewStar } from '@/components/customer/icons/ReviewStar';
import {
  GOOGLE_REVIEWS,
  starRatingToNumber,
  type GoogleReview,
} from '@/lib/googleReviews';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Image from 'next/image';

const INITIAL_REVIEW_COUNT = 6;
const REVIEWS_PER_PAGE = 6;
const COMMENT_PREVIEW_LENGTH = 280;
const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', {
  numeric: 'auto',
});
const avatarColors = [
  'bg-[#195AFF]',
  'bg-[#7C3AED]',
  'bg-[#0F766E]',
  'bg-[#C2410C]',
  'bg-[#BE185D]',
];
const STAFF_REVIEW_PATTERN =
  /\bintern(ship)?\b|\bplace to work\b|\bworking here\b|\bwork here\b|\bas an? employee\b|\bemployee\b|\bcolleagues?\b|\bcareer growth\b|\blearning environment\b|\bwork culture\b|\bi(?:'m| am)\s+working\b|\bmanagement is encouraging\b/i;

function Stars({
  rating,
  size = 'sm',
}: {
  rating: number;
  size?: 'sm' | 'lg';
}) {
  const dimension = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <div
      className='flex items-center gap-[2px]'
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < Math.round(rating) ? '' : 'opacity-20'}>
          <span className={`inline-block ${dimension}`}>
            <ReviewStar />
          </span>
        </span>
      ))}
    </div>
  );
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return 'Date unavailable';

  const timestamp = new Date(iso).getTime();
  if (!Number.isFinite(timestamp)) return 'Date unavailable';

  const seconds = (timestamp - Date.now()) / 1000;
  const elapsed = Math.abs(seconds);
  if (elapsed < 60)
    return relativeTimeFormatter.format(Math.round(seconds), 'second');
  if (elapsed < 60 * 60)
    return relativeTimeFormatter.format(Math.round(seconds / 60), 'minute');
  if (elapsed < 24 * 60 * 60)
    return relativeTimeFormatter.format(Math.round(seconds / 3600), 'hour');
  if (elapsed < 30 * 24 * 60 * 60)
    return relativeTimeFormatter.format(Math.round(seconds / 86400), 'day');
  if (elapsed < 365 * 24 * 60 * 60)
    return relativeTimeFormatter.format(Math.round(seconds / 2592000), 'month');
  return relativeTimeFormatter.format(Math.round(seconds / 31536000), 'year');
}

function getAvatarColor(name: string): string {
  const hash = Array.from(name).reduce(
    (value, character) => value + character.charCodeAt(0),
    0,
  );
  return avatarColors[hash % avatarColors.length];
}

function isStaffReview(review: GoogleReview): boolean {
  return STAFF_REVIEW_PATTERN.test(review.comment);
}

function ReviewCard({ review }: { review: GoogleReview }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const rating = starRatingToNumber(review.star_rating);
  const reviewerName = review.reviewer_name || 'Google reviewer';
  const initial = reviewerName.charAt(0).toUpperCase();
  const comment = review.comment?.trim() ?? '';
  const isLongComment = comment.length > COMMENT_PREVIEW_LENGTH;
  const displayedComment =
    isLongComment && !isExpanded
      ? `${comment.slice(0, COMMENT_PREVIEW_LENGTH).trimEnd()}…`
      : comment;

  return (
    <article className='rounded-[12px] border border-[#E1E1E1] bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'>
      <div className='mb-3 flex items-center gap-3'>
        {review.reviewer_photo_url ? (
          <img
            src={review.reviewer_photo_url}
            alt=''
            className='h-10 w-10 flex-shrink-0 rounded-full object-cover'
            referrerPolicy='no-referrer'
          />
        ) : (
          <div
            aria-hidden='true'
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-base font-medium text-white ${getAvatarColor(reviewerName)}`}
          >
            {initial}
          </div>
        )}
        <div className='min-w-0'>
          <p className='truncate font-Schibsted_Grotesk text-[14px] font-medium leading-[120%] text-[#090D28]'>
            {reviewerName}
          </p>
          <p className='text-[12px] leading-[120%] text-[#8C8C8C]'>
            {formatRelativeDate(review.create_time)}
          </p>
        </div>
      </div>

      <div className='mb-3 flex items-center gap-2'>
        <Stars rating={rating} />
        <span className='text-[12px] text-[#8C8C8C]'>{rating} out of 5</span>
      </div>

      {comment && (
        <p className='whitespace-pre-line text-[14px] leading-[150%] text-[#454545]'>
          {displayedComment}
        </p>
      )}
      {isLongComment && (
        <button
          type='button'
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
          className='mt-2 font-Schibsted_Grotesk text-[14px] font-medium text-[#195AFF] underline underline-offset-2'
        >
          {isExpanded ? 'Less' : 'More'}
        </button>
      )}

      {review.reply && (
        <div className='mt-4 border-l-2 border-[#D8E3FF] bg-[#F8F9FB] py-3 pl-4 pr-3'>
          <p className='mb-1 text-[12px] font-semibold text-[#090D28]'>
            Response from Vilva Networks
          </p>
          <p className='whitespace-pre-line text-[13px] leading-[140%] text-[#666]'>
            {review.reply.comment}
          </p>
        </div>
      )}
    </article>
  );
}

export default function GoogleReviews() {
  const [visibleCount, setVisibleCount] = useState(INITIAL_REVIEW_COUNT);
  const [modalVisibleCount, setModalVisibleCount] =
    useState(INITIAL_REVIEW_COUNT);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [quoteStartIndex, setQuoteStartIndex] = useState(0);

  const quoteReviews = [...GOOGLE_REVIEWS]
    .filter((review) => review.comment?.trim() && !isStaffReview(review))
    .sort(
      (a, b) =>
        starRatingToNumber(b.star_rating) - starRatingToNumber(a.star_rating) ||
        b.comment.length - a.comment.length,
    );

  useEffect(() => {
    if (quoteReviews.length <= 2) return;

    const intervalId = window.setInterval(() => {
      setQuoteStartIndex(
        (currentIndex) => (currentIndex + 2) % quoteReviews.length,
      );
    }, 30_000);

    return () => window.clearInterval(intervalId);
  }, [quoteReviews.length]);

  if (GOOGLE_REVIEWS.length === 0) return null;

  const reviews = [...GOOGLE_REVIEWS].sort(
    (a, b) =>
      (Date.parse(b.create_time ?? '') || 0) -
      (Date.parse(a.create_time ?? '') || 0),
  );
  const ratedReviews = reviews.filter(
    (review) => starRatingToNumber(review.star_rating) > 0,
  );
  const averageRating =
    ratedReviews.length > 0
      ? ratedReviews.reduce(
          (total, review) => total + starRatingToNumber(review.star_rating),
          0,
        ) / ratedReviews.length
      : null;
  const ratingCounts = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: reviews.filter(
      (review) => starRatingToNumber(review.star_rating) === rating,
    ).length,
  }));
  const highestCount = Math.max(1, ...ratingCounts.map(({ count }) => count));
  const visibleQuoteReviews = Array.from(
    { length: Math.min(2, quoteReviews.length) },
    (_, offset) =>
      quoteReviews[(quoteStartIndex + offset) % quoteReviews.length],
  );
  const visibleReviews = reviews.slice(0, visibleCount);
  const modalReviews = reviews.slice(0, modalVisibleCount);

  return (
    <section className='w-full py-10 md:py-14'>
      <h2 className='mb-6 text-[24px] font-semibold leading-[120%] text-[#090D28] md:text-[30px]'>
        What our customers say
      </h2>

      <div className='grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.45fr)] lg:gap-10'>
        <aside className='self-start rounded-[16px] border border-[#E1E1E1] bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] lg:sticky lg:top-24 lg:p-6'>
          <div className='flex items-center gap-3'>
            <span className='text-[48px] font-semibold leading-none tracking-tight text-[#090D28]'>
              {averageRating === null ? '—' : averageRating.toFixed(1)}
            </span>
            <div>
              <Stars rating={averageRating ?? 0} size='lg' />
              <p className='mt-1 text-[13px] text-[#666]'>
                {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
              </p>
            </div>
            <Image
              src='/images/assets/Google-rating-4.8.webp'
              alt='Google rating: 4.8 out of 5 stars'
              width={1880}
              height={837}
              className='ml-auto h-auto w-[88px] shrink-0 object-contain sm:w-[100px]'
            />
          </div>

          <div className='mt-6 space-y-2' aria-label='Rating breakdown'>
            {ratingCounts.map(({ rating, count }) => (
              <div
                key={rating}
                className='flex items-center gap-2 text-[13px] text-[#555]'
              >
                <span className='flex w-7 shrink-0 items-center gap-1'>
                  <span>{rating}</span>
                  <span className='inline-block h-3 w-3'>
                    <ReviewStar />
                  </span>
                </span>
                <div
                  role='progressbar'
                  aria-label={`${rating} star reviews`}
                  aria-valuemin={0}
                  aria-valuemax={reviews.length}
                  aria-valuenow={count}
                  className='h-2 flex-1 overflow-hidden rounded-full bg-[#E9E9E9]'
                >
                  <div
                    className='h-full rounded-full bg-[#F4B400]'
                    style={{ width: `${(count / highestCount) * 100}%` }}
                  />
                </div>
                <span className='w-6 text-right tabular-nums'>{count}</span>
              </div>
            ))}
          </div>

          {quoteReviews.length > 0 && (
            <div className='mt-6 hidden space-y-3 border-t border-[#EAEAEA] pt-5 lg:block'>
              {visibleQuoteReviews.map((review, index) => (
                <blockquote
                  key={`${review.reviewer_name}-${review.create_time}-${index}`}
                >
                  <p className='line-clamp-3 text-[13px] italic leading-[145%] text-[#454545]'>
                    “{review.comment.trim()}”
                  </p>
                  <footer className='mt-2 flex items-center gap-2 font-Schibsted_Grotesk text-[12px] font-medium text-[#777]'>
                    {review.reviewer_photo_url ? (
                      <img
                        src={review.reviewer_photo_url}
                        alt=''
                        className='h-6 w-6 rounded-full object-cover'
                        referrerPolicy='no-referrer'
                      />
                    ) : (
                      <span
                        aria-hidden='true'
                        className={`flex h-6 w-6 font-Schibsted_Grotesk items-center justify-center rounded-full text-[10px] text-white ${getAvatarColor(review.reviewer_name || 'Google reviewer')}`}
                      >
                        {(review.reviewer_name || 'Google reviewer')
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    )}
                    <span className='font-Schibsted_Grotesk'>— {review.reviewer_name || 'Google reviewer'}</span>
                  </footer>
                </blockquote>
              ))}
            </div>
          )}

          <a
            href='https://www.google.com/maps/search/?api=1&query=Vilva+Networks+Chennai'
            target='_blank'
            rel='noopener noreferrer'
            className='mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#195AFF] px-5 py-3 text-center font-Schibsted_Grotesk text-[14px] font-semibold text-white transition-colors hover:bg-[#1248D8]'
          >
            Write a review
          </a>
        </aside>

        <div className='min-w-0'>
          <div className='space-y-4 lg:hidden'>
            <ReviewCard
              key={`${reviews[0].reviewer_name}-${reviews[0].create_time}`}
              review={reviews[0]}
            />
            {reviews.length > 1 && (
              <button
                type='button'
                onClick={() => {
                  setModalVisibleCount(INITIAL_REVIEW_COUNT);
                  setIsMobileModalOpen(true);
                }}
                className='w-full rounded-full border border-[#195AFF] px-5 py-3 text-[14px] font-semibold text-[#195AFF] transition-colors hover:bg-[#F2F6FF]'
              >
                View more reviews
              </button>
            )}
          </div>

          <div className='google-reviews-scrollbar hidden space-y-4 lg:block lg:max-h-[80vh] lg:overflow-y-auto lg:overscroll-contain lg:pr-2'>
            {visibleReviews.map((review, index) => (
              <ReviewCard
                key={`${review.reviewer_name}-${review.create_time}-${index}`}
                review={review}
              />
            ))}
            {visibleCount < reviews.length && (
              <button
                type='button'
                onClick={() =>
                  setVisibleCount((count) => count + REVIEWS_PER_PAGE)
                }
                className='w-full rounded-full border border-[#195AFF] px-5 py-3 text-[14px] font-semibold font-Schibsted_Grotesk text-[#195AFF] transition-colors hover:bg-[#F2F6FF]'
              >
                Show more reviews
              </button>
            )}
          </div>

          <Dialog open={isMobileModalOpen} onOpenChange={setIsMobileModalOpen}>
            <DialogContent className='flex max-h-[90dvh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl border-0 bg-white p-0'>
              <DialogHeader className='shrink-0 border-b border-[#EAEAEA] px-5 py-4 pr-12 text-left'>
                <DialogTitle className='text-lg font-semibold text-[#090D28]'>
                  Customer reviews
                </DialogTitle>
                <DialogDescription className='text-sm text-[#666]'>
                  {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                </DialogDescription>
              </DialogHeader>
              <div className='min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4'>
                {modalReviews.map((review, index) => (
                  <ReviewCard
                    key={`${review.reviewer_name}-${review.create_time}-${index}`}
                    review={review}
                  />
                ))}
                {modalVisibleCount < reviews.length && (
                  <button
                    type='button'
                    onClick={() =>
                      setModalVisibleCount((count) => count + REVIEWS_PER_PAGE)
                    }
                    className='w-full rounded-full border border-[#195AFF] px-5 py-3 text-[14px] font-semibold font-Schibsted_Grotesk text-[#195AFF] transition-colors hover:bg-[#F2F6FF]'
                  >
                    Show more reviews
                  </button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}
